import { useEffect, useState } from "react";
import { useApp } from "../../contexts/AppContext";

const API_BASE = import.meta.env?.VITE_API_URL ?? "/api";
// Commit SHA baked into THIS build (fallback for local dev, where /version.json
// isn't served). VITE_GIT_SHA <- deploy.yml / release.yml.
const BUILT_SHA = import.meta.env?.VITE_GIT_SHA ?? "dev";

const short = (s) =>
  s && s !== "dev" && s !== "unknown" ? s.slice(0, 7) : s || "dev";

// A release tag looks like a version (v1.2.3 / 1.2.3); anything else (a bootstrap
// label, empty) is treated as "no release" and we fall back to the commit hash.
const isReleaseTag = (t) => !!t && /^v?\d+\.\d+/.test(t);

// Admin-only build-version strip pinned to the bottom of the app shell. Leads with
// the deployed VERSION — the release tag when set (e.g. v1.0.1), otherwise the
// commit hash — read from the deployed /version.json manifest. Also shows the API
// commit so an admin can spot a frontend/API mismatch. Hidden for non-admins.
export default function Footer() {
  const { currentUserType } = useApp();
  const [info, setInfo] = useState(null); // { tag, sha } from /version.json
  const [apiSha, setApiSha] = useState(null);

  const isAdmin = currentUserType === "admin";

  useEffect(() => {
    if (!isAdmin) return;
    let alive = true;
    // Deployed frontend manifest: release tag + commit (written by release.yml).
    fetch("/version.json", { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => alive && setInfo(d))
      .catch(() => {});
    // Backend commit — for the frontend/API mismatch check.
    fetch(`${API_BASE}/version`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => alive && setApiSha(d?.sha ?? "unknown"))
      .catch(() => alive && setApiSha("unreachable"));
    return () => {
      alive = false;
    };
  }, [isAdmin]);

  if (!isAdmin) return null;

  const tag = info?.tag;
  const sha = info?.sha ?? BUILT_SHA;
  const released = isReleaseTag(tag);
  const version = released ? tag : short(sha);
  const mismatch =
    apiSha && apiSha !== "unreachable" && short(apiSha) !== short(sha);

  return (
    <footer className="flex shrink-0 items-center gap-4 border-t border-(--border-subtle) bg-(--surface-1) px-[30px] py-2 text-[11px] text-(--text-muted)">
      <span>
        version{" "}
        <code className="font-mono text-(--text-primary)">{version}</code>
        {released ? (
          <span className="ml-1 text-(--text-muted)">({short(sha)})</span>
        ) : null}
      </span>
      <span>
        api{" "}
        <code className="font-mono text-(--text-primary)">
          {apiSha ? short(apiSha) : "…"}
        </code>
      </span>
      {mismatch ? (
        <span
          className="font-semibold text-amber-600"
          title="Frontend and API are running different builds"
        >
          ⚠ version mismatch
        </span>
      ) : null}
    </footer>
  );
}
